import React, { useContext, useEffect, useMemo } from 'react';
import { CheckCircleOutlined, CopyOutlined } from '@ant-design/icons';
import FormItem from 'antd/es/form/FormItem';
import { Alert, Button, Collapse, Divider, Flex, Input, message } from 'antd';
import { useDebounce } from '@leedpulse-monorepo/hooks';
import StripePayment from '../components/stripe/StripePayment';
import SignupFormContext from '../../../contexts/forms/SignupFormContext';
import { AccountType, billingIntervalAsSuffix, EBillingInterval } from '@leedpulse-monorepo/enums';
import { createPortal } from 'react-dom';
import { IPlan } from '@leedpulse-monorepo/interfaces';
// @ts-expect-error: No types available for react-copy-to-clipboard
import { CopyToClipboard } from "react-copy-to-clipboard";

interface StepActivationCodeProps {
  onActivationCodeChange: (value: string) => void;
  isValidActivationCode: boolean;
  stepTitle: string;
  submitError: string | null;
  selectedPlanId: string;
  selectedPlanData: IPlan | undefined;
  businessDomainLabel: string;
  professionalNumber: string;
}

const LEEDFLOW_APP_URL = import.meta.env.VITE_LEEDFLOW_FRONTEND_URL;

const StepActivationCode: React.FC<StepActivationCodeProps> = ({
  onActivationCodeChange,
  isValidActivationCode,
  stepTitle,
  submitError,
  selectedPlanId,
  selectedPlanData,
  businessDomainLabel,
  professionalNumber,
}) => {
  const { data, actions } = useContext(SignupFormContext);
  const { formData, accountCreated, paymentSuccessful, submitButtonTarget, isLoading, signupJWT } = data;
  const { handleConfirmAccount, setSubmitError } = actions;

  // La valeur arrive en argument du callback : la lire depuis
  // `debouncedActivationCode`, declare sur cette meme ligne, etait un piege TDZ
  // (react/immutability « read during its own initialization »).
  const [debouncedActivationCode, setDebouncedActivationCode] = useDebounce("", 500, (value) => {
    onActivationCodeChange(value);
  });

  /**
   * Handle direct account confirmation for supervisor (COMPANY) accounts
   * that don't require payment.
   */
  const handleDirectConfirm = async () => {
    try {
      await handleConfirmAccount();
    } catch (error) {
      console.error('Error confirming account:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Une erreur est survenue lors de la confirmation du compte'
      );
    }
  };

  const isSupervisor = formData.accountType === AccountType.COMPANY;
  const isSubscription = formData.accountType === AccountType.SUBSCRIPTION;

  const signupLink = useMemo(() =>
    `${LEEDFLOW_APP_URL}/signup?token=${signupJWT}`,
    [signupJWT]
  );

  useEffect(() => {
    if (formData.activationCode) {
      setDebouncedActivationCode(formData.activationCode);
    }

  }, []);

  // Account fully confirmed (after payment or direct confirmation)
  if (accountCreated && paymentSuccessful) {
    return (
      <div className="signup__step-content signup__step-content--success">
        <div className="signup__success-icon">
          <CheckCircleOutlined />
        </div>
        <h2 className="signup__step-title">Compte créé !</h2>
        <p className="signup__step-subtitle">Le compte leedflow a été créé avec succès.</p>
        <p className="signup__step-subtitle">Un mail contenant vos identifiants a été envoyé à votre adresse email, si vous aviez déjà un compte Leedpulse, vos identifiants restent inchangés.</p>
        <Alert style={{ marginBottom: '8px' }} message="Vous bénéficiez d'une session d'introduction à Leedflow, vous devez réserver un créneau avec Anthony en cliquant sur le lien ci-dessous :" />
        </div>
    );
  }

  return (
    <div className="signup__step-content">
      <h2 className="signup__step-title">{stepTitle}</h2>
      <p className="signup__step-subtitle">Dernière étape pour activer votre compte Leedflow</p>

      {/* Activation code input — locked once validated */}
      <FormItem
        label="Code d'activation"
        layout='vertical'
        validateStatus={submitError ? 'error' : undefined}
        help={submitError ? submitError : undefined}
      >
        <Input
          value={debouncedActivationCode}
          onChange={(e) => setDebouncedActivationCode(e.target.value.toUpperCase())}
          placeholder="XXXXXX"
          autoFocus={!isValidActivationCode}
          status={submitError ? 'error' : undefined}
          disabled={isValidActivationCode}
          suffix={isValidActivationCode ? <CheckCircleOutlined style={{ color: '#22c55e' }} /> : undefined}
        />
        {
          signupJWT ? <>
            <Divider size='small' />
            <CopyToClipboard text={signupLink} onCopy={() => message.success("Lien d'inscription copié")}>
              <Flex justify="center" align="center" gap={5}>
                <Input value={signupLink} readOnly />
                <Button type="dashed" icon={<CopyOutlined />}>
                </Button>
              </Flex>
            </CopyToClipboard>
          </> :
            null
        }
      </FormItem>


      <Collapse
        className='signup__summary'
        defaultActiveKey={['1']}
        items={[
          {
            key: '1',
            label: <span style={{
              color: '#2D3436',
              fontWeight: '600',
              fontSize: '16px'
            }}>Récapitulatif de vos informations</span>,
            style: {
              backgroundColor: '#f9fafb !important',
            },
            children: (
              <>
                <div className="signup__summary-item">
                  <span className="signup__summary-label">Nom complet</span>
                  <span className="signup__summary-value">
                    {formData.firstName} {formData.lastName}
                  </span>
                </div>
                <div className="signup__summary-item">
                  <span className="signup__summary-label">Téléphone</span>
                  <span className="signup__summary-value">{formData.phone}</span>
                </div>
                <div className="signup__summary-item">
                  <span className="signup__summary-label">Email</span>
                  <span className="signup__summary-value">{formData.email}</span>
                </div>
                <div className="signup__summary-item">
                  <span className="signup__summary-label">Société</span>
                  <span className="signup__summary-value">{formData.companyName}</span>
                </div>
                <div className="signup__summary-item">
                  <span className="signup__summary-label">SIRET</span>
                  <span className="signup__summary-value">{formData.companySiret}</span>
                </div>
                <div className="signup__summary-item">
                  <span className="signup__summary-label">Domaine</span>
                  <span className="signup__summary-value">{businessDomainLabel}</span>
                </div>
                <div className="signup__summary-item">
                  <span className="signup__summary-label">Formule</span>
                  <span className="signup__summary-value">
                    {selectedPlanData?.name || '-'} - {selectedPlanData?.price?.formatted || '0€'}{billingIntervalAsSuffix(selectedPlanData?.price?.interval ?? EBillingInterval.MONTH)}
                  </span>
                </div>
                {formData.activityDescription && (
                  <div className="signup__summary-item signup__summary-item--full">
                    <span className="signup__summary-label">Description</span>
                    <span className="signup__summary-value">{formData.activityDescription}</span>
                  </div>
                )}</>
            ),
          },
        ]}>
      </Collapse>

      {/* SUBSCRIPTION flow: show Stripe payment form (outside FormItem for proper layout) */}
      {isValidActivationCode && accountCreated?.company.id && isSubscription && (
        <StripePayment
          planId={selectedPlanId}
          companyId={accountCreated.company.id}
          companyApiKey={accountCreated.company.api_key}
          userUid={accountCreated.userUid}
          professionalNumber={formData.professionalNumber}
        />
      )}

      {/* COMPANY (supervisor) flow: direct account creation button via portal */}
      {isValidActivationCode && accountCreated?.company.id && isSupervisor && submitButtonTarget?.current && createPortal(
        <Button
          type="primary"
          onClick={handleDirectConfirm}
          loading={isLoading}
        >
          Créer mon compte
        </Button>,
        submitButtonTarget.current
      )}
    </div>
  );
};

export default StepActivationCode;
